import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Custom hook for Plausible analytics
export const useAnalytics = () => {
  const location = useLocation();
  const previousPath = useRef();

  // Track page views automatically when location changes
  useEffect(() => {
    // Only track if plausible is available and path actually changed
    if (window.plausible && location.pathname !== previousPath.current) {
      // Map paths to readable page names
      const getPageName = (pathname) => {
        switch (pathname) {
          case '/':
            return 'CC Jensen Form';
          case '/hire':
            return 'Equipment Hire';
          case '/admin':
            return 'Admin Login';
          case '/admin/dashboard':
            return 'Admin Dashboard';
          case '/admin/users':
            return 'User Management';
          case '/admin/archived-submissions':
            return 'Archived Submissions';
          default:
            if (pathname.startsWith('/admin/submissions/')) {
              return 'Submission Detail';
            }
            return 'Unknown Page';
        }
      };

      const pageName = getPageName(location.pathname);
      
      // Track page view with custom event
      window.plausible('pageview', {
        props: {
          page: pageName,
          path: location.pathname
        }
      });

      previousPath.current = location.pathname;
    }
  }, [location]);

  // Function to track custom events
  const trackEvent = (eventName, props = {}) => {
    if (window.plausible) {
      window.plausible(eventName, { props });
    }
  };

  // Specific tracking functions for common events
  const trackFormStep = (stepNumber, stepName) => {
    trackEvent('Form Step', {
      step: stepNumber,
      stepName: stepName
    });
  };

  const trackFormSubmission = (success, errorMessage = null) => {
    trackEvent('Form Submission', {
      success: success ? 'true' : 'false',
      error: errorMessage || 'none'
    });
  };

  const trackFileUpload = (fileType, success, fileName = null) => {
    trackEvent('File Upload', {
      type: fileType,
      success: success ? 'true' : 'false',
      fileName: fileName || 'unknown'
    });
  };

  const trackEquipmentInquiry = (unitName, unitType) => {
    trackEvent('Equipment Inquiry', {
      unit: unitName,
      type: unitType
    });
  };

  const trackEquipmentFilter = (filterType, filterValue) => {
    trackEvent('Equipment Filter', {
      filter: filterType,
      value: filterValue
    });
  };

  const trackROIInterest = (action) => {
    trackEvent('ROI Interest', {
      action: action // 'modal_open', 'phone_click', 'email_click'
    });
  };

  const trackNavigation = (from, to) => {
    trackEvent('Navigation', {
      from: from,
      to: to
    });
  };

  const trackRecaptcha = (success) => {
    trackEvent('Recaptcha', {
      success: success ? 'true' : 'false'
    });
  };

  const trackBrochureDownload = (source) => {
    trackEvent('Brochure Download', {
      source: source // 'hire_page', 'form_page', etc.
    });
  };

  return {
    trackEvent,
    trackFormStep,
    trackFormSubmission,
    trackFileUpload,
    trackEquipmentInquiry,
    trackEquipmentFilter,
    trackROIInterest,
    trackNavigation,
    trackRecaptcha,
    trackBrochureDownload
  };
};
